import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    CheckCircleIcon,
    InformationCircleIcon,
    ShieldCheckIcon,
    SparklesIcon,
    CalendarDaysIcon,
    CreditCardIcon
} from '@heroicons/react/24/outline';
import {cn} from '../utils/style';

export function TabbyLogo({className = "h-6"}) {
    return (
        <svg
            className={className}
            viewBox="0 0 106 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect width="106" height="40" rx="8" fill="#3EEDB4" fillOpacity="0.15" />
            <path
                d="M19.8 14H15.6V17.8H12V21.4H15.6V26.2C15.6 28.5 17.1 29.8 19.5 29.8C20.6 29.8 21.6 29.5 22.2 29.1V25.7C21.8 25.9 21.3 26 20.8 26C19.9 26 19.8 25.5 19.8 24.6V21.4H22.4V17.8H19.8V14Z"
                fill="#0F172A"
            />
            <path
                d="M32.8 17.8H29V19.4C28.3 18.3 27 17.5 25.2 17.5C21.8 17.5 19.5 20.2 19.5 23.8C19.5 27.4 22 30.1 25.4 30.1C27.1 30.1 28.4 29.3 29.1 28.2V29.8H32.8V17.8ZM26.2 26.7C24.3 26.7 23.1 25.4 23.1 23.8C23.1 22.2 24.3 20.9 26.2 20.9C28.1 20.9 29.3 22.2 29.3 23.8C29.3 25.4 28.1 26.7 26.2 26.7Z"
                fill="#0F172A"
            />
            <path
                d="M42.7 17.5C41 17.5 39.7 18.3 39 19.4V13.8H35.3V29.8H39V28.2C39.7 29.3 41 30.1 42.7 30.1C46.1 30.1 48.6 27.4 48.6 23.8C48.6 20.2 46.1 17.5 42.7 17.5ZM41.9 26.7C40 26.7 38.8 25.4 38.8 23.8C38.8 22.2 40 20.9 41.9 20.9C43.8 20.9 45 22.2 45 23.8C45 25.4 43.8 26.7 41.9 26.7Z"
                fill="#0F172A"
            />
            <path
                d="M58.3 17.5C56.6 17.5 55.3 18.3 54.6 19.4V13.8H50.9V29.8H54.6V28.2C55.3 29.3 56.6 30.1 58.3 30.1C61.7 30.1 64.2 27.4 64.2 23.8C64.2 20.2 61.7 17.5 58.3 17.5ZM57.5 26.7C55.6 26.7 54.4 25.4 54.4 23.8C54.4 22.2 55.6 20.9 57.5 20.9C59.4 20.9 60.6 22.2 60.6 23.8C60.6 25.4 59.4 26.7 57.5 26.7Z"
                fill="#0F172A"
            />
            <path
                d="M69.8 26.7L65.6 17.8H61.5L67.8 30.3L64.7 36.8H68.8L77.7 17.8H73.7L69.8 26.7Z"
                fill="#0F172A"
            />
            <circle cx="87" cy="20" r="4" fill="#3EEDB4" />
        </svg>
    );
}

export default function TabbyCheckout({
    invoice,
    address,
    setAddress,
    city,
    setCity,
    zipCode,
    setZipCode,
    className
}) {
    const {t, i18n} = useTranslation();
    const [showInfo, setShowInfo] = useState(false);

    const totalAmount = parseFloat(invoice?.totalAmount || invoice?.amount || 0);
    const currency = invoice?.currency?.symbol || invoice?.currency?.name || 'AED';
    const installmentAmount = (totalAmount / 4).toFixed(2);

    // Dynamic Tabby promo snippet loader
    useEffect(() => {
        if (!invoice?.tabbyPublicKey) return;

        const scriptId = 'tabby-promo-script';
        if (!document.getElementById(scriptId)) {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = 'https://checkout.tabby.ai/tabby-promo.js';
            script.async = true;
            script.onload = () => {
                try {
                    if (window.TabbyPromo) {
                        new window.TabbyPromo({
                            selector: '#tabby-promo-widget',
                            currency: invoice?.currency?.name || 'AED',
                            price: totalAmount,
                            installmentsCount: 4,
                            lang: i18n.language || 'en',
                            source: 'checkout',
                            publicKey: invoice.tabbyPublicKey,
                            merchantCode: invoice?.tabbyMerchantCode || ''
                        });
                    }
                } catch (e) {
                    console.debug('Tabby official widget fallback used:', e);
                }
            };
            document.body.appendChild(script);
        }
    }, [invoice?.tabbyPublicKey, invoice?.currency?.name, invoice?.tabbyMerchantCode, totalAmount, i18n.language]);

    // Installment payment schedule dates
    const getInstallmentDate = (monthsToAdd) => {
        const d = new Date();
        d.setMonth(d.getMonth() + monthsToAdd);
        return d.toLocaleDateString(i18n.language === 'ar' ? 'ar-AE' : 'en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className={cn("overflow-hidden rounded-3xl bg-white border border-emerald-100 shadow-xl shadow-emerald-950/5 transition-all", className)}>
            {/* Tabby Header Banner */}
            <div className="bg-gradient-to-r from-emerald-900 via-teal-800 to-slate-900 px-6 py-5 text-white">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="bg-white/95 rounded-xl px-2.5 py-1.5 shadow-sm">
                            <TabbyLogo className="h-6 w-auto" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-bold text-white text-base tracking-tight">
                                    {t('pay.tabby_pay_in_4', 'Split in 4 interest-free payments')}
                                </span>
                                <span className="bg-emerald-400/20 text-emerald-300 border border-emerald-400/30 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full">
                                    {t('pay.tabby_zero_interest', '0% Interest')}
                                </span>
                            </div>
                            <p className="text-emerald-100/70 text-xs mt-0.5">
                                {t('pay.tabby_subtitle', 'No hidden fees. Shariah-compliant. Instant approval.')}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => setShowInfo(!showInfo)}
                        className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-100 transition-colors font-semibold px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10"
                    >
                        <InformationCircleIcon className="h-4 w-4" />
                        <span>{t('pay.how_tabby_works', 'How it works')}</span>
                    </button>
                </div>
            </div>

            {/* How Tabby Works Expandable Drawer */}
            {showInfo && (
                <div className="bg-emerald-50/70 border-b border-emerald-100 px-6 py-5 animate-fade-in text-slate-800">
                    <div className="text-xs font-bold uppercase tracking-wider text-emerald-900 mb-3 flex items-center gap-1.5">
                        <SparklesIcon className="h-4 w-4 text-emerald-600" />
                        {t('pay.tabby_how_it_works_title', 'How Tabby Split in 4 works')}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                        <div className="bg-white p-3 rounded-2xl border border-emerald-100/60 shadow-sm">
                            <div className="font-bold text-emerald-900 mb-1">1. {t('pay.tabby_step_1_title', 'Choose Tabby')}</div>
                            <div className="text-slate-600 leading-relaxed">{t('pay.tabby_step_1_desc', 'Select Tabby at checkout and link any debit or credit card.')}</div>
                        </div>
                        <div className="bg-white p-3 rounded-2xl border border-emerald-100/60 shadow-sm">
                            <div className="font-bold text-emerald-900 mb-1">2. {t('pay.tabby_step_2_title', 'Pay 25% today')}</div>
                            <div className="text-slate-600 leading-relaxed">{t('pay.tabby_step_2_desc', 'Pay only 1/4th of your purchase today and your order is confirmed immediately.')}</div>
                        </div>
                        <div className="bg-white p-3 rounded-2xl border border-emerald-100/60 shadow-sm">
                            <div className="font-bold text-emerald-900 mb-1">3. {t('pay.tabby_step_3_title', 'Automatic payments')}</div>
                            <div className="text-slate-600 leading-relaxed">{t('pay.tabby_step_3_desc', 'Remaining 3 payments are automatically split across the next 3 months. No fees, no interest.')}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Official Tabby Promo widget slot or native interactive schedule */}
            <div className="p-6">
                <div id="tabby-promo-widget" className="mb-4 empty:hidden" />

                {/* 4 Installments Schedule Grid */}
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                            <CalendarDaysIcon className="h-4 w-4 text-emerald-600" />
                            {t('pay.tabby_payment_schedule', 'Payment Schedule')}
                        </span>
                        <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                            4 × {installmentAmount} {currency}
                        </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {/* Step 1: Today */}
                        <div className="bg-emerald-50/80 border-2 border-emerald-500 rounded-2xl p-3.5 relative overflow-hidden shadow-sm">
                            <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded-bl-lg">
                                {t('pay.today', 'Today')}
                            </div>
                            <div className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                                {t('pay.1st_payment', '1st payment')}
                            </div>
                            <div className="text-base font-black text-emerald-950 mt-1">
                                {installmentAmount} <span className="text-xs font-medium text-emerald-700">{currency}</span>
                            </div>
                            <div className="text-[10px] text-emerald-700 mt-0.5 flex items-center gap-1 font-medium">
                                <CheckCircleIcon className="h-3 w-3 text-emerald-600" />
                                {t('pay.due_today', 'Due today (25%)')}
                            </div>
                        </div>

                        {/* Step 2: 1 Month */}
                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 shadow-sm">
                            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                {t('pay.2nd_payment', '2nd payment')}
                            </div>
                            <div className="text-base font-black text-slate-900 mt-1">
                                {installmentAmount} <span className="text-xs font-medium text-slate-500">{currency}</span>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5 font-medium">
                                {t('pay.in_1_month', 'In 1 month')} ({getInstallmentDate(1)})
                            </div>
                        </div>

                        {/* Step 3: 2 Months */}
                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 shadow-sm">
                            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                {t('pay.3rd_payment', '3rd payment')}
                            </div>
                            <div className="text-base font-black text-slate-900 mt-1">
                                {installmentAmount} <span className="text-xs font-medium text-slate-500">{currency}</span>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5 font-medium">
                                {t('pay.in_2_months', 'In 2 months')} ({getInstallmentDate(2)})
                            </div>
                        </div>

                        {/* Step 4: 3 Months */}
                        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 shadow-sm">
                            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                                {t('pay.4th_payment', '4th payment')}
                            </div>
                            <div className="text-base font-black text-slate-900 mt-1">
                                {installmentAmount} <span className="text-xs font-medium text-slate-500">{currency}</span>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5 font-medium">
                                {t('pay.in_3_months', 'In 3 months')} ({getInstallmentDate(3)})
                            </div>
                        </div>
                    </div>
                </div>

                {/* Address details input for Tabby session */}
                <div className="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 sm:p-5 mb-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3 flex items-center gap-1.5">
                        <CreditCardIcon className="h-4 w-4 text-slate-500" />
                        {t('pay.tabby_billing_details', 'Billing / Shipping Details for Tabby')}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                        <div className="sm:col-span-6">
                            <label className="block text-xs font-bold text-slate-700 mb-1">
                                {t('invoice.address', 'Address')}
                            </label>
                            <input
                                type="text"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                placeholder={invoice?.address || t('pay.enter_address', 'Street address / Area')}
                                className="w-full text-xs rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20 py-2.5 px-3 bg-white"
                            />
                        </div>

                        <div className="sm:col-span-3">
                            <label className="block text-xs font-bold text-slate-700 mb-1">
                                {t('invoice.city', 'City')}
                            </label>
                            <input
                                type="text"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder={invoice?.city || t('pay.enter_city', 'City (e.g. Dubai)')}
                                className="w-full text-xs rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20 py-2.5 px-3 bg-white"
                            />
                        </div>

                        <div className="sm:col-span-3">
                            <label className="block text-xs font-bold text-slate-700 mb-1">
                                {t('invoice.zip_code', 'Zip / Postal Code')}
                            </label>
                            <input
                                type="text"
                                value={zipCode}
                                onChange={(e) => setZipCode(e.target.value)}
                                placeholder={invoice?.zipCode || '00000'}
                                className="w-full text-xs rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20 py-2.5 px-3 bg-white"
                            />
                        </div>
                    </div>
                </div>

                {/* Trust Badges */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-3 text-[11px] text-slate-500">
                    <div className="flex items-center gap-1.5">
                        <ShieldCheckIcon className="h-4 w-4 text-emerald-600" />
                        <span>{t('pay.tabby_shariah_certified', 'Shariah-compliant and secure')}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400">
                        <span>{t('pay.tabby_instant_approval', 'Instant approval with Emirates ID / National ID')}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
